import { BatchedMesh, Vector2, Vector3, type Intersection, type Object3D, type Raycaster } from 'three';

import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { POI_EDGE_HIT_RADIUS_PX } from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';
import type { RefObject } from 'react';

function isVisible(mesh: BatchedMesh): boolean {
    for (let object: Object3D | null = mesh; object; object = object.parent) {
        if (!object.visible) {
            return false;
        }
    }
    return true;
}

export function raycastPoiPoints(this: BatchedMesh, raycaster: Raycaster, intersections: Intersection[]) {
    if (!isVisible(this)) {
        return;
    }
    const offset = intersections.length;
    BatchedMesh.prototype.raycast.call(this, raycaster, intersections);
    for (let index = offset; index < intersections.length; index++) {
        // R3F deduplicates by instanceId, not Three's batchId.
        intersections[index].instanceId = intersections[index].batchId;
    }
}

export function createPoiFaceRaycast(
    entries: PoiBatchedEntry[],
    instanceIdsRef: RefObject<number[]>,
    viewport: { width: number; height: number }
) {
    const start = new Vector3();
    const end = new Vector3();
    const rayPoint = new Vector3();
    const edgePoint = new Vector3();
    const cameraPoint = new Vector3();
    const projectedStart = new Vector3();
    const projectedEnd = new Vector3();
    const screenStart = new Vector2();
    const screenSegment = new Vector2();
    const screenPointer = new Vector2();
    const hits: Intersection[] = [];
    const cornerIds = new Set<number>();
    const cornerEntryIndices = entries.flatMap((entry, index) =>
        entry.filterType === POIFilterType.Corner ? [index] : []
    );

    return function (this: BatchedMesh, raycaster: Raycaster, intersections: Intersection[]) {
        if (!isVisible(this)) {
            return;
        }

        hits.length = 0;
        const instanceIds = instanceIdsRef.current;
        cornerIds.clear();
        for (const index of cornerEntryIndices) {
            if (instanceIds[index] !== undefined) {
                cornerIds.add(instanceIds[index]);
            }
        }
        this.userData.poiCornerBatchIds = cornerIds;
        BatchedMesh.prototype.raycast.call(this, raycaster, hits);
        for (const hit of hits) {
            if (hit.batchId === undefined || !cornerIds.has(hit.batchId)) {
                hit.instanceId = hit.batchId;
                intersections.push(hit);
            }
        }

        const camera = raycaster.camera;
        if (!camera) {
            return;
        }
        cameraPoint.set(0, 0, -1).unproject(camera).applyMatrix4(camera.matrixWorldInverse);
        const nearZ = cameraPoint.z;
        raycaster.ray.at(1, rayPoint).project(camera);
        screenPointer.set((rayPoint.x * viewport.width) / 2, (rayPoint.y * viewport.height) / 2);

        for (const index of cornerEntryIndices) {
            const entry = entries[index];
            const batchId = instanceIds[index];
            if (batchId === undefined || !this.getVisibleAt(batchId)) {
                continue;
            }

            start.set(0, -0.5, 0).applyMatrix4(entry.matrix).applyMatrix4(this.matrixWorld);
            end.set(0, 0.5, 0).applyMatrix4(entry.matrix).applyMatrix4(this.matrixWorld);
            const startZ = cameraPoint.copy(start).applyMatrix4(camera.matrixWorldInverse).z;
            const endZ = cameraPoint.copy(end).applyMatrix4(camera.matrixWorldInverse).z;
            if (startZ > nearZ && endZ > nearZ) {
                continue;
            }
            if (startZ > nearZ) {
                start.lerp(end, (nearZ - startZ) / (endZ - startZ));
            } else if (endZ > nearZ) {
                end.lerp(start, (nearZ - endZ) / (startZ - endZ));
            }
            projectedStart.copy(start).project(camera);
            projectedEnd.copy(end).project(camera);
            screenStart.set((projectedStart.x * viewport.width) / 2, (projectedStart.y * viewport.height) / 2);
            screenSegment.set(
                ((projectedEnd.x - projectedStart.x) * viewport.width) / 2,
                ((projectedEnd.y - projectedStart.y) * viewport.height) / 2
            );
            const lengthSq = screenSegment.lengthSq();
            const t =
                lengthSq === 0
                    ? 0
                    : Math.max(
                          0,
                          Math.min(
                              1,
                              ((screenPointer.x - screenStart.x) * screenSegment.x +
                                  (screenPointer.y - screenStart.y) * screenSegment.y) /
                                  lengthSq
                          )
                      );
            screenStart.addScaledVector(screenSegment, t);
            if (screenStart.distanceToSquared(screenPointer) > POI_EDGE_HIT_RADIUS_PX ** 2) {
                continue;
            }
            // Screen interpolation is perspective-corrected before reporting the world-space hit.
            const startDepth = -cameraPoint.copy(start).applyMatrix4(camera.matrixWorldInverse).z;
            const endDepth = -cameraPoint.copy(end).applyMatrix4(camera.matrixWorldInverse).z;
            const worldT =
                camera.projectionMatrix.elements[15] === 0
                    ? (t * startDepth) / ((1 - t) * endDepth + t * startDepth)
                    : t;
            edgePoint.lerpVectors(start, end, worldT);
            if (cameraPoint.copy(edgePoint).project(camera).z > 1) {
                continue;
            }
            const distance = raycaster.ray.origin.distanceTo(edgePoint);
            if (distance >= raycaster.near && distance <= raycaster.far) {
                intersections.push({ distance, point: edgePoint.clone(), object: this, batchId, instanceId: batchId });
            }
        }
    };
}
